import styled from '@emotion/styled';
import { Grid, GridProps, TextField } from '@mui/material';
import { InboundLineItem } from '@tyrio/dto';
import { useInboundInvoiceCtx } from '../../../context/InboundInvoiceContext';
import { formatCurrencyWithoutSymbol } from '../../../helpers/currency-format';
import { useCalculatePricePerRow } from '../helpers/calculation';
import { useState } from 'react';

interface ItemProps {
  lineItem: InboundLineItem;
  productIdx: number;
  itemIdx: number;
}

const renderTextField = (
  value: number,
  setValue: (updatedField: string, updatedValue: number) => void,
  key: string,
  sx: {
    marginLeft?: string;
    backgroundColor?: string;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
  },
  gridProps: GridProps,
  setChangingSellingPrice?: (a: boolean) => void,
  type?: 'stockIn' | 'purchaseOrder' | '',
  maxQty?: number
) => {
  return (
    <Grid {...gridProps} key={key}>
      <TextField
        variant="outlined"
        size="small"
        type="number"
        sx={{
          ...sx,
          width: '',
          marginLeft: 0,
        }}
        value={
          Number(value) !== 0 && Number(value) - Math.floor(Number(value)) === 0
            ? Number(value).toString().replace(/^0+/, '')
            : Number(value) - Math.floor(Number(value)) !== 0
            ? Number(value)
            : key === 'quantity'
            ? 1
            : 0
        }
        InputProps={{
          inputProps: {
            min: key === 'quantity' ? 1 : 0,
            max:
              key === 'quantity' && type === 'purchaseOrder' && maxQty
                ? maxQty
                : 2000,
          },
        }}
        onChange={(e) => {
          if (setChangingSellingPrice)
            if (key === 'sellingPrice') setChangingSellingPrice(true);
            else setChangingSellingPrice(false);

          setValue(key, Number(e.target.value));
        }}
      />
    </Grid>
  );
};

export const Item = ({ lineItem, productIdx, itemIdx }: ItemProps) => {
  const { setPreparedInvoices, preparedInvoices, input } =
    useInboundInvoiceCtx();

  const [changingSellingPrice, setChangingSellingPrice] = useState(false);

  const { itemsPrice, grandTotal } = useCalculatePricePerRow(
    lineItem,
    preparedInvoices?.vatChargeType ?? 'V05',
    changingSellingPrice
  );

  const handleLineItemChange = (updatedField: string, updatedValue: number) => {
    if (preparedInvoices) {
      const updatedInvoice = { ...preparedInvoices };
      if (updatedInvoice) {
        const updatedItems = [...(preparedInvoices.items ?? [])];

        const updatedLineItems = [...updatedItems[itemIdx].lineItems];

        updatedLineItems[productIdx] = {
          ...updatedLineItems[productIdx],
          [updatedField]:
            updatedField === 'quantity' && updatedValue === 0
              ? 1
              : updatedValue,
        };

        updatedItems[itemIdx] = {
          ...updatedItems[itemIdx],
          lineItems: updatedLineItems,
        };

        updatedInvoice.items = updatedItems;

        setPreparedInvoices(updatedInvoice);
      }
    }
  };

  return (
    <Wrapper>
      <ProductDetailsWrapper>
        <ProductDetailsText>{lineItem.productName}</ProductDetailsText>
        <ProductDescription>{`EAN: ${lineItem.ean}   IPC: ${lineItem.ipc}    Supplier code: ${lineItem.supplierCode}`}</ProductDescription>
      </ProductDetailsWrapper>
      <Wrap>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            maxWidth: 'calc(100% - 240px)',
            minWidth: 'calc(100% - 240px)',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          {renderTextField(
            lineItem.quantity,
            handleLineItemChange,
            'quantity',
            {
              marginLeft: '5px',
              backgroundColor: 'white',
              minWidth: '60px',
              maxWidth: '80px',
            },
            { xs: 2, lg: 2 },
            setChangingSellingPrice,
            input.type,
            lineItem.maxQty
          )}

          {renderTextField(
            lineItem.invoicePrice
              ? Number(lineItem.invoicePrice.toFixed(4))
              : Number(lineItem.purchasePrice.toFixed(4)),
            handleLineItemChange,
            'purchasePrice',
            { marginLeft: '5px', backgroundColor: 'white', minWidth: '85px' },
            { xs: 4, lg: 4 },
            setChangingSellingPrice
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '80px',
            }}
          >
            {renderTextField(
              Number(lineItem.rebate.toFixed(2)),
              handleLineItemChange,
              'rebate',
              {
                marginLeft: '5px',
                backgroundColor: 'white',
                minWidth: '60px',
              },
              { xs: 3, lg: 3 },
              setChangingSellingPrice
            )}

            <Text>%</Text>
          </div>

          <Grid xs={3} lg={3}>
            <Text style={{ width: '70px' }}>
              {formatCurrencyWithoutSymbol(lineItem.variableCost)}
            </Text>
          </Grid>

          {preparedInvoices?.vatChargeType === 'V05D' && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {renderTextField(
                Number(lineItem.customsPer.toFixed(5)),
                handleLineItemChange,
                'customsPer',
                {
                  marginLeft: '5px',
                  backgroundColor: 'white',
                  minWidth: '60px',
                },
                { xs: 3, lg: 3 },
                setChangingSellingPrice
              )}

              <Text>%</Text>
            </div>
          )}

          <Grid xs={3} lg={3}>
            <Text style={{ width: '70px' }}>
              {formatCurrencyWithoutSymbol(itemsPrice)}
            </Text>
          </Grid>
        </div>
        <DiscountWrapper>
          <div
            style={{
              display: 'flex',
              minWidth: '60px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {renderTextField(
              Number(lineItem.margin.toFixed(2)),
              handleLineItemChange,
              'margin',
              {
                backgroundColor: 'white',
                minWidth: '50px',
              },
              { xs: 3, lg: 3 },
              setChangingSellingPrice
            )}

            <Text>%</Text>
          </div>
          <InputWrapper>
            {renderTextField(
              Number(lineItem.sellingPrice.toFixed(2)),
              handleLineItemChange,
              'sellingPrice',
              {
                backgroundColor: 'white',
                minWidth: '70px',
              },
              { xs: 3, lg: 3 },
              setChangingSellingPrice
            )}
          </InputWrapper>
          <InputWrapper>
            <Text>{formatCurrencyWithoutSymbol(grandTotal)}</Text>
          </InputWrapper>
        </DiscountWrapper>
      </Wrap>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  max-height: 72px;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 2px 16px 16px;
  border-bottom: 1px solid #dfe3e8;
`;

const ProductDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 70px;
  gap: 5px;
  white-space: nowrap;
  overflow-x: auto;
  width: calc(100% - 400px);
`;

const Text = styled.div`
  font-family: Barlow;
  font-weight: 400;
  font-size: 16px;
  display: flex;
  justify-content: center;
`;

const ProductDetailsText = styled.div`
  font-family: Barlow;
  font-weight: 400;
  max-height: 40px;
`;

export const ProductDescription = styled.div`
  font-family: Barlow;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  color: #919eab;
`;

export const Wrap = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
`;

const DiscountWrapper = styled.div`
  min-width: 240px;
  width: auto;
  height: 68px;
  background-color: #f4f6f8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
`;

const InputWrapper = styled.div`
  min-width: 60px;
  display: flex;
  justify-content: center;
`;
